import {
  CLOSE_CODES,
  CLOSE_REASONS,
  FRAME_FIELDS,
  PROTOCOL_VERSION,
  type Frame,
} from "../protocol/protocol";

/**
 * What a close means for the next step. Every close but 4410 is quiet: reconnect with a fresh
 * ticket and backoff. A 4403 mostly needs nothing special: the next ticket answers it (a 403 there
 * means the assistant is unavailable to this user). One whose reason says the user's AI access
 * was just removed is that answer already: unavailable, with no ticket asked for.
 */
export type CloseAction = "reconnect" | "banner" | "unavailable";

export function closeAction(code: number, reason = ""): CloseAction {
  if (code === CLOSE_CODES.tab_closed) return "banner";
  if (code === CLOSE_CODES.forbidden && reason === CLOSE_REASONS.access_off) return "unavailable";
  return "reconnect";
}

export const BACKOFF = { minMs: 500, maxMs: 30_000 } as const;

/** Exponential backoff with full jitter and no attempt cap. */
export function backoffDelay(attempt: number, random: () => number): number {
  const cap = Math.min(BACKOFF.maxMs, BACKOFF.minMs * 2 ** attempt);
  return Math.round(random() * cap);
}

const FRAME_TYPES = new Set(Object.keys(FRAME_FIELDS));

/** One inbound message as a frame, or null for anything that is not one of ours. */
export function parseFrame(data: unknown): Frame | null {
  if (typeof data !== "string") return null;
  let value: unknown;
  try {
    value = JSON.parse(data);
  } catch {
    return null;
  }
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const frame = value as { v?: unknown; type?: unknown };
  if (frame.v !== PROTOCOL_VERSION || typeof frame.type !== "string") return null;
  return FRAME_TYPES.has(frame.type) ? (value as Frame) : null;
}

/** A command id: what the server dedupes sends on and echoes back. [A-Za-z0-9_-]{1,64}. */
export function newCid(seed: string): string {
  return seed.replace(/[^A-Za-z0-9_-]/g, "").slice(0, 64);
}
